import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, Footer } from '../components';
import GameCard from '../components/GameCard';
import CategoryModal from '../components/CategoryModal';
import { getRisks } from '../lib/api/risksApi';

const TABS = ['danger', 'delisted', 'always_online', 'server_risk', 'safe'];

// RiskViewModel (API) -> forme attendue par GameCard.
const toCard = (e) => ({
  appId: e.steamAppId,
  name: e.name,
  riskType: e.riskType,
  severity: e.severity,
  notes: e.notes,
  gogUrl: e.gogUrl,
  playtimeMinutes: 0,
  // Sur la liste, on montre le lien GOG dès qu'il existe (y compris pour les "safe").
  savable: !!e.gogUrl,
});

const readType = () => {
  const p = new URLSearchParams(window.location.search).get('type');
  return TABS.includes(p) ? p : 'danger';
};

const tabLabel = (t, type) => {
  if (type === 'danger') return t('list.tab_danger');
  if (type === 'safe') return t('list.tab_safe');
  return t(`risk.${type}`);
};

function RisksListPage() {
  const { t } = useTranslation();
  const [type, setType] = useState(readType);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [modal, setModal] = useState(null); // { item } -> popup détail d'un jeu

  const load = useCallback((selType, selPage, selQ) => {
    setLoading(true);
    getRisks({ type: selType, q: selQ, page: selPage, pageSize: 24 })
      .then(({ data }) => {
        setTotal(data.total || 0);
        setItems((prev) =>
          selPage === 1 ? (data.items || []) : [...prev, ...(data.items || [])],
        );
      })
      .catch(() => {
        if (selPage === 1) setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Debounce de la saisie : on n'appelle l'API que 300 ms après la dernière touche.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    load(type, 1, debounced);
    setPage(1);
  }, [type, debounced, load]);

  const selectTab = (nextType) => {
    if (nextType === type) return;
    const url = `${window.location.pathname}?type=${nextType}`;
    window.history.replaceState({}, '', url);
    setType(nextType);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(type, next, debounced);
  };

  return (
    <div className="app-shell">
      <Header />
      <main className="app-main">
        <section className="risks-page">
          <a className="risks-page__back" href="/">{t('list.back')}</a>
          <h1 className="risks-page__title">{t('list.title')}</h1>

          <div className="risks-tabs">
            {TABS.map((tb) => (
              <button
                key={tb}
                type="button"
                className={`risks-tab risks-tab--${tb} ${tb === type ? 'is-active' : ''}`}
                onClick={() => selectTab(tb)}
              >
                {tabLabel(t, tb)}
              </button>
            ))}
          </div>

          <div className="risks-search-wrap">
            <input
              type="search"
              className="risks-search"
              placeholder={t('list.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {items.length > 0 ? (
            <>
              <div className="verdict__grid">
                {items.map((e) => (
                  <GameCard
                    key={e.steamAppId}
                    item={toCard(e)}
                    onExplain={(g) => setModal({ item: g })}
                  />
                ))}
              </div>
              {items.length < total && (
                <button
                  type="button"
                  className="btn btn--ghost risks-page__more"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {t('list.more')} ({items.length}/{total})
                </button>
              )}
            </>
          ) : (
            <p className="risks-page__empty">
              {loading ? '…' : t('list.empty')}
            </p>
          )}

          <CategoryModal modal={modal} verdict={{}} onClose={() => setModal(null)} />
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default RisksListPage;
